import BaseLayer from '@scenes/base/BaseLayer'

import PackFileLoader from '@engine/loaders/PackFileLoader'


export default class WidgetManager extends BaseLayer {

    constructor(scene) {
        super(scene)

        this.depth = -1

        this.activeWidgets = {}
        this.loadedPacks = []

        this.lastLoadingKey = null

        this.packLoader = new PackFileLoader(scene)

        this.packLoader.on('progress', progress =>
            this.setLoadingProgress(progress)
        )

        this.packLoader.on('filecomplete', (key, type) =>
            this.onFileComplete(key, type)
        )
    }

    get activeWidgetObjects() {
        return Object.values(this.activeWidgets)
    }

    get isLoadingVisible() {
        return this.interface.prompt.loading.visible
    }

    onFileComplete(key, type) {
        if (type === 'packfile') {
            this.loadedPacks.push(key)
        }
    }

    async loadWidget(key, floatingLayer, args = null) {
        if (!this.keyExists(key)) {
            return
        }

        if (this.keyActive(key)) {
            const widget = this.getWidget(key)

            if (!widget) {
                return
            }

            if (floatingLayer && widget.floatingLayer !== floatingLayer) {
                widget.floatingLayer = floatingLayer
            }

            if (typeof widget.show === 'function') {
                widget.show(args)
            } else {
                widget.visible = true
            }

            return
        }

        this.lastLoadingKey = key

        const { path, preload } = this.crumbs.widgets[key]

        this.showLoading(this.getString(key))

        const widgetModule = await this.loadWidgetClass(path)

        if (!widgetModule) {
            return
        }

        const callback = () => this.onWidgetLoaded(key, widgetModule.default, floatingLayer, args)

        const modulePreload = widgetModule?.preload ?? widgetModule?.default?.preload ?? null
        const crumbPreload = preload ?? null
        const preloadKey = modulePreload?.key ?? crumbPreload?.key ?? null
        const preloadUrls = this.getPreloadUrls(modulePreload, crumbPreload)

        if (!preloadKey || preloadUrls.length === 0) {
            callback()
            return
        }

        this.loadPackWithFallback(preloadKey, preloadUrls, () => callback())
    }

    async loadWidgetClass(path) {
        try {
            const widgetClass = await (import(
                /* webpackInclude: /\.js$/ */
                `@scenes/${path}`
            ))

            return widgetClass

        } catch (error) {
            console.error(error)

            this.closeLoading()

            return null
        }
    }

    onWidgetLoaded(key, widgetClass, floatingLayer, args) {
        const createWidget = this.isLoadingVisible && key === this.lastLoadingKey

        // Floating widgets skip createWidget check
        if (!floatingLayer && !createWidget) {
            return
        }

        if (key === this.lastLoadingKey) {
            this.closeLoading()
        }

        this.createWidget(key, widgetClass, floatingLayer, args)
    }

    createWidget(key, widgetClass, floatingLayer, args) {
        const scene = floatingLayer
            ? floatingLayer.scene
            : this.scene

        const widget = new widgetClass(scene)

        // Expose the originating widget key so dynamic widgets can tailor their behavior
        widget.widgetKey = key
        if (typeof widget.setWidgetContext === 'function') {
            widget.setWidgetContext({ key, floatingLayer, args })
        }

        this.activeWidgets[key] = widget

        if (floatingLayer) {
            widget.floatingLayer = floatingLayer
        } else {
            this.add(widget)
        }

        if (typeof widget.show === 'function') {
            widget.show(args)
        } else if (args && typeof widget.setData === 'function') {
            widget.setData(args)
        }

        this.events.emit(`create_widget_${key.toLowerCase()}`)
    }

    showLoading(text) {
        this.interface.prompt.showLoading(text)
    }

    closeLoading() {
        this.interface.prompt.loading.close()
    }

    setLoadingProgress(progress) {
        this.interface.prompt.loading.setProgress(progress)
    }

    keyExists(key) {
        return key in this.crumbs.widgets
    }

    keyActive(key) {
        return key in this.activeWidgets
    }

    getWidget(key) {
        return this.activeWidgets[key]
    }

    findWidget(filter) {
        return this.activeWidgetObjects.filter(filter)
    }

    removeWidget(widget) {
        for (const key in this.activeWidgets) {
            if (this.activeWidgets[key] === widget) {
                delete this.activeWidgets[key]
            }
        }
    }

    closeWidgets() {
        for (const widget of this.activeWidgetObjects) {
            widget.close()
        }
    }

    unloadWidgets() {
        this.closeWidgets()

        this.memory.unloadPacks(this.loadedPacks)

        this.loadedPacks = []
    }

    getPreloadUrls(...configs) {
        const urls = []

        for (const config of configs) {
            if (!config) {
                continue
            }

            if (Array.isArray(config.urls)) {
                urls.push(...config.urls)
            } else if (config.url) {
                urls.push(config.url)
            }
        }

        return urls
            .map(url => (typeof url === 'string' ? url.trim() : ''))
            .filter(url => url.length > 0)
            .filter((url, index, array) => array.indexOf(url) === index)
    }

    loadPackWithFallback(key, urls, onComplete, attempted = []) {
        if (!Array.isArray(urls) || urls.length === 0) {
            onComplete()
            return
        }

        const [currentUrl, ...remaining] = urls
        const attemptedUrls = [...attempted, currentUrl]

        this.packLoader.loadPack(
            key,
            currentUrl,
            () => onComplete(),
            () => {
                if (remaining.length === 0) {
                    console.warn(`[WidgetManager] Failed to load pack "${key}" from URLs: ${attemptedUrls.join(', ')}`)
                    onComplete()
                    return
                }

                this.loadPackWithFallback(key, remaining, onComplete, attemptedUrls)
            }
        )
    }

}
